import { __awaiter } from "tslib";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from '../types';
import { MISSING_CUSTOMER, MISSING_CUSTOMER_TOKEN } from '@open-tender/types';
import { makeCustomerProfile, makeFavoritesLookup } from '@open-tender/utils';
import { showNotification } from '../notifications';
import { resetCustomerAllergens, setCustomerAllergens } from './allergens';
import { setSelectedAllergens } from '../allergens';
import { resetCustomerFavorites, setCustomerFavorites, setCustomerFavoritesLookup } from './favorites';
import { resetCustomerCreditCards } from './creditCards';
import { resetCustomerAddresses } from './addresses';
import { resetCustomerHouseAccounts } from './houseAccounts';
import { resetCustomerLoyalty } from './loyalty';
import { resetCustomerOrder } from './order';
import { resetCustomerOrders } from './orders';
import { resetCustomerRewards } from './rewards';
import { resetCustomerCommunicationPreferences } from './communicationPreferences';
import { resetCustomerGiftCards, setCustomerGiftCards } from './giftCards';
import { addMessage, resetOrder, setAlert } from '../order';
import { resetCheckout, updateCheckoutCustomer } from '../checkout';
import { resetGroupOrder } from '../groupOrder';
import { resetGuest } from '../guest';
import { resetCustomerTpls } from './tpls';
const initialState = {
    auth: null,
    profile: null,
    loading: 'idle',
    error: null
};
export var AccountActionType;
(function (AccountActionType) {
    AccountActionType["LoginCustomer"] = "customer/loginCustomer";
    AccountActionType["LogoutCustomer"] = "customer/logoutCustomer";
    AccountActionType["FetchCustomer"] = "customer/fetchCustomer";
    AccountActionType["UpdateCustomer"] = "customer/updateCustomer";
    AccountActionType["VerifyCustomer"] = "customer/verifyCustomer";
    AccountActionType["LinkPosToken"] = "customer/linkPosToken";
    AccountActionType["DeleteCustomer"] = "customer/deleteCustomer";
    AccountActionType["LoginCustomerThanx"] = "customer/loginCustomerThanx";
    AccountActionType["AuthCustomerThanx"] = "customer/authCustomerThanx";
})(AccountActionType || (AccountActionType = {}));
export const checkAuth = (err, dispatch, rejectWithValue) => __awaiter(void 0, void 0, void 0, function* () {
    if ((err === null || err === void 0 ? void 0 : err.status) === 401) {
        yield dispatch(logoutCustomer());
        dispatch(addMessage('Please login to reauthenticate your account'));
        return null;
    }
    else {
        return rejectWithValue();
    }
});
export const fetchCustomer = createAsyncThunk(AccountActionType.FetchCustomer, 
// eslint-disable-next-line no-unused-vars
(_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const customer = yield api.getCustomer(token);
        const { allergens, gift_cards, favorites } = customer;
        dispatch(setCustomerAllergens(allergens || []));
        dispatch(setSelectedAllergens(allergens || []));
        dispatch(setCustomerGiftCards(gift_cards || []));
        dispatch(setCustomerFavorites(favorites || []));
        const lookup = makeFavoritesLookup(favorites);
        dispatch(setCustomerFavoritesLookup(lookup || {}));
        const profile = makeCustomerProfile(customer);
        return profile;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const loginCustomer = createAsyncThunk(AccountActionType.LoginCustomer, ({ email, password, recaptcha_token }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const auth = yield api.postLogin(email, password, recaptcha_token);
        dispatch(setCustomerAuth(auth));
        yield dispatch(fetchCustomer());
        return;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const logoutCustomer = createAsyncThunk(AccountActionType.LogoutCustomer, (isReset, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const { api } = getState().config;
        const token = selectToken(getState());
        if (!token)
            throw new Error(undefined);
        if (isReset) {
            dispatch(resetOrder());
            dispatch(resetCheckout());
            dispatch(setSelectedAllergens(null));
        }
        yield api.postLogout(token);
        dispatch(updateCheckoutCustomer(null));
        dispatch(resetCustomerAddresses());
        dispatch(resetCustomerAllergens());
        dispatch(resetCustomerCreditCards());
        dispatch(resetCustomerFavorites());
        dispatch(resetCustomerGiftCards());
        dispatch(resetCustomerHouseAccounts());
        dispatch(resetCustomerLoyalty());
        dispatch(resetCustomerOrder());
        dispatch(resetCustomerOrders());
        dispatch(resetGroupOrder());
        dispatch(resetCustomerRewards());
        dispatch(resetCustomerCommunicationPreferences());
        dispatch(resetGuest());
        dispatch(resetCustomerTpls());
        return null;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const linkPosToken = createAsyncThunk(AccountActionType.LinkPosToken, (posToken, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.postCustomerPosToken(token, posToken);
        dispatch(showNotification('Order successfully linked!'));
        dispatch(fetchCustomer());
        return;
    }
    catch (err) {
        const error = err;
        dispatch(addMessage((error === null || error === void 0 ? void 0 : error.detail) || (error === null || error === void 0 ? void 0 : error.message)));
        return rejectWithValue(error);
    }
}));
export const updateCustomer = createAsyncThunk(AccountActionType.UpdateCustomer, ({ data, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const token = selectToken(getState());
        const api = getState().config.api;
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const customer = yield api.putCustomer(token, data);
        const profile = makeCustomerProfile(customer);
        dispatch(showNotification('Account updated!'));
        if (callback)
            callback(data);
        return profile;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const sendCustomerVerificationEmail = createAsyncThunk(AccountActionType.VerifyCustomer, (linkUrl, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token) {
            dispatch(addMessage('Missing customer token'));
            throw new Error(MISSING_CUSTOMER_TOKEN);
        }
        yield api.postSendVerificationEmail(token, linkUrl);
        dispatch(showNotification('Verification email sent!'));
        return;
    }
    catch (err) {
        dispatch(addMessage(err.detail || err.message));
        return rejectWithValue(err);
    }
}));
export const loginCustomerThanx = createAsyncThunk(AccountActionType.LoginCustomerThanx, ({ email, origin, recaptcha_token }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        yield api.postThanxLogin(email, origin, recaptcha_token);
        dispatch(setAlert({ type: 'close' }));
        dispatch(addMessage('Thanks! Please check your email on this device.'));
        return;
    }
    catch (err) {
        const errr = err;
        if (errr) {
            const error = (errr === null || errr === void 0 ? void 0 : errr.params) ? errr.params['$.email'] : null;
            if (error && error.includes("'email'")) {
                errr.detail = 'Please enter a valid email address';
            }
        }
        return rejectWithValue(errr);
    }
}));
export const authCustomerThanx = createAsyncThunk(AccountActionType.AuthCustomerThanx, (data, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const auth = yield api.postThanxAuth(data.code, data.path, data.origin);
        dispatch(setCustomerAuth(auth));
        yield dispatch(fetchCustomer());
        return;
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
export const deleteCustomer = createAsyncThunk(AccountActionType.DeleteCustomer, ({ data, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const token = selectToken(getState());
        const api = getState().config.api;
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.deleteCustomer(token);
        yield api.postLogout(token);
        dispatch(updateCheckoutCustomer(null));
        dispatch(resetCustomerAddresses());
        dispatch(resetCustomerAllergens());
        dispatch(resetCustomerCreditCards());
        dispatch(resetCustomerFavorites());
        dispatch(resetCustomerGiftCards());
        dispatch(resetCustomerHouseAccounts());
        dispatch(resetCustomerLoyalty());
        dispatch(resetCustomerOrder());
        dispatch(resetCustomerOrders());
        dispatch(resetGroupOrder());
        dispatch(resetCustomerRewards());
        dispatch(resetCustomerCommunicationPreferences());
        dispatch(resetGuest());
        dispatch(showNotification('Account deleted!'));
        if (callback)
            callback(data);
        return;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const accountSlice = createSlice({
    name: ReducerType.Account,
    initialState,
    reducers: {
        resetCustomer: () => initialState,
        resetLoginError: state => {
            state.loading = 'idle';
            state.error = null;
        },
        setCustomerAuth: (state, action) => {
            state.auth = action.payload;
            // state.loading = 'idle'
            // state.error = null
        }
    },
    extraReducers: builder => {
        builder
            .addCase(loginCustomer.fulfilled, state => {
            // state.auth = action.payload
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(loginCustomer.pending, state => {
            state.loading = 'pending';
        })
            .addCase(loginCustomer.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(fetchCustomer.fulfilled, (state, action) => {
            state.profile = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomer.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomer.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(updateCustomer.fulfilled, (state, action) => {
            state.profile = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(updateCustomer.pending, state => {
            state.loading = 'pending';
        })
            .addCase(updateCustomer.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(logoutCustomer.fulfilled, () => initialState)
            .addCase(logoutCustomer.pending, state => {
            state.loading = 'pending';
        })
            .addCase(logoutCustomer.rejected, () => initialState)
            .addCase(sendCustomerVerificationEmail.fulfilled, state => {
            state.loading = 'idle';
        })
            .addCase(sendCustomerVerificationEmail.pending, state => {
            state.loading = 'pending';
        })
            .addCase(sendCustomerVerificationEmail.rejected, state => {
            state.loading = 'idle';
        })
            .addCase(linkPosToken.fulfilled, () => {
            //do nothing
        })
            .addCase(linkPosToken.pending, () => {
            //do nothing
        })
            .addCase(linkPosToken.rejected, () => {
            //do nothing
        })
            .addCase(deleteCustomer.fulfilled, () => initialState)
            .addCase(deleteCustomer.pending, state => {
            state.loading = 'pending';
        })
            .addCase(deleteCustomer.rejected, () => initialState)
            .addCase(loginCustomerThanx.fulfilled, state => {
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(loginCustomerThanx.pending, state => {
            state.loading = 'pending';
        })
            .addCase(loginCustomerThanx.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        })
            .addCase(authCustomerThanx.fulfilled, state => {
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(authCustomerThanx.pending, state => {
            state.loading = 'pending';
        })
            .addCase(authCustomerThanx.rejected, (state, action) => {
            state.loading = 'idle';
            state.error = action.payload;
        });
    }
});
export const { resetCustomer, resetLoginError, setCustomerAuth } = accountSlice.actions;
export const selectToken = (state) => state.customer.account.auth ? state.customer.account.auth.access_token : null;
export const selectCustomer = (state) => state.customer.account;
export const accountReducer = accountSlice.reducer;
